<template>
  <bimini-default-section :section="section" :menu="menu" menu-item-component="bimini-steak-item"
                          section-modifier="steak"
                          class="menu--section-bimini_steak"
                          :class="{'menu--section-wagyu': isWagyuSection}"></bimini-default-section>
</template>

<script lang="ts">
/**
 * Custom component for Bimini steak section.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.1.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2023-03-23 (1.0.0) (jhoward)
 *      - <biminiSteakSection> adds customizations for the Bimini steak menu section.
 *
 *    2023-03-27 (1.0.1) (jhoward)
 *      - Fix menu version placement in <biminiSteakSection>.
 *
 *    2025-04-08 (1.2.0) (jhoward)
 *    - Replace "menuVersion" prop with "menu" prop <bimini-steak-section>.
 *    - Remove "visual_" and "textual_" prefixes from class names in <bimini-steak-section>.
 */

import {defineComponent, PropType} from "vue";
import BiminiDefaultSection from "./Bimini_defaultSection.vue";
import {Menu, MenuSection} from "../defs";

export default defineComponent({
  name: 'Bimini_steakSection',
  components: {BiminiDefaultSection},
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
  },
  computed: {
    isWagyuSection(): boolean {
      return /(^|\s)wagyu(\s|$)/i.test(this.section.ms_title);
    }
  },
})
</script>

<style scoped>
.menu--section-bimini_steak:deep(.menu--section_title),
.visual_menu--container-landscape .menu--section-bimini_steak:deep(.menu--section_title ) {
  padding-bottom: 0;
  margin-top: 2em;
}

.menu--section-bimini_steak.menu--section-wagyu {
  position: relative;
}

.menu--section-bimini_steak:not(.menu--section-wagyu):deep(.menu--item_subgroup:nth-child(1)::before),
.menu--section-bimini_steak:not(.menu--section-wagyu):deep(.menu--item_subgroup:last-child::before),
.menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport .menu--item_subgroup::before),
.menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport:last-child .menu--item_subgroup::before) {
  content: '';
  position: absolute;
  top: 1em;
  right: 11em;
  width: 5em;
  height: 5em;
  background-repeat: no-repeat;
  background-size: contain;
  background-image: url(/library/images/menu-tags/tag-niman-ranch-128.png);
}

.menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport + .viewport:not(:last-child) .menu--item_subgroup::before) {
  display: none;
}

.menu--section-bimini_steak:not(.menu--section-wagyu):deep(.menu--item_subgroup:last-child)::before,
.menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport:last-child .menu--item_subgroup::before) {
  left: auto;
  right: 9.4em;
  width: 8em;
  height: 8em;
  background-image: url(/library/images/menu-tags/tag-prime-128.png);
}

.menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport .menu--item_subgroup::before) {
  right: 4em;
  top: 2.5em;
}

.menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport:last-child .menu--item_subgroup::before) {
  right: 2.5em;
  top: 2em;
}

@media (max-width: 600px) {
  .menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport .menu--item_subgroup::before) {
    right: 3em;
    top: 4em;
    width: 4em;
    height: 4em;
  }

  .menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport:last-child .menu--item_subgroup::before) {
    right: 2.5em;
    top: 7em;
    width: 6em;
    height: 6em;
  }
}

@media (max-width: 400px) {
  .menu--section-bimini_steak.textual_menu--section:not(.menu--section-wagyu):deep(.viewport .menu--item_subgroup::before) {
    display: none;
  }
}

.menu--section-bimini_steak:deep(.menu_version--container) {
  bottom: -7em;
}

.menu--section-bimini_steak:deep(.menu--item_subgroup) {
  position: relative;
  grid-gap: 0.1em;
}

.menu--section-bimini_steak:deep(h3),
.menu--section-bimini_steak:deep(p) {
  color: #3e2603;
}

@media (prefers-contrast: more) {
  .menu--section-bimini_steak:deep(h3),
  .menu--section-bimini_steak:deep(p) {
    color: #000;
  }
}
</style>